import { useEffect, useMemo, useState } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { Info, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/authStore";
import { CONSOLE_PERMISSIONS } from "@/constants/permissions";
import { nutritionAPI } from "@/lib/nutrition-api";
import { getApiErrorMessage, getApiErrorStatus } from "@/lib/api";
import type { FoodItemPayload } from "@/lib/nutrition-api";
import {
  buildNutrientValues,
  getFoodTypeLabel,
  FOOD_TYPE_OPTIONS,
  FOOD_NUTRIENT_BASIS_OPTIONS,
  FOOD_NUTRIENT_BASIS_LABELS,
  SUPPLEMENT_FOOD_TYPE,
  validateServingMultiplier,
  validateServingWeightG,
  type FoodNutrientBasis,
} from "@/lib/nutrition-form-utils";
import { useNutrientCatalog } from "@/hooks/useNutrientCatalog";
import type { NutrientCategory } from "@/hooks/useNutrientCatalog";
import { useTagMetadata } from "@/hooks/useNutritionDropdowns";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { invalidateQueries } from "@/lib/query-keys";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  EditableNutrientTable,
  type EditableNutrientField,
} from "@/components/settings/EditableNutrientTable";
import { FoodServingsCard } from "@/components/settings/FoodServingsCard";
import { ServingUnitCombobox } from "@/components/settings/ServingUnitCombobox";
import { TagPickerRow } from "./TagPickerRow";
import { AccessDeniedCard, RequiredMark } from "./nutrition-shared";

// ─────────────────────────────────────────────────────────────────────
// Field definitions — naming aligned with UpdateFoodEntryForm so both
// forms can eventually share schema/payload helpers.
// ─────────────────────────────────────────────────────────────────────

const nameFields = [
  { name: "name", label: "Food Name" },
  { name: "hindiName", label: "Hindi Name" },
  { name: "fullName", label: "Full Name" },
] as const;

const booleanFields = [
  { name: "favourite", label: "Favorite" },
  { name: "homemade", label: "Homemade" },
] as const;

// Schema accepts the fixed scalar fields explicitly and allows any extra
// string keys for the nutrient grid — those are enumerated at runtime from
// the catalog and registered via FormField. `.catchall()` keeps Zod from
// stripping them and lets `Object.fromEntries(...)` defaults persist.
const foodEntrySchema = z
  .object({
    name: z.string().min(1, "Food name is required"),
    hindiName: z.string().optional(),
    fullName: z.string().optional(),
    foodType: z.string().optional(),
    // Only supplements may be stored per-serving, and the basis is frozen the
    // moment the food is created — so it has to be a deliberate choice here,
    // not a default nobody saw.
    nutrientBasis: z.string().optional(),
    // The food's first serving, sent nested inside the create call. Only ever
    // filled in for a per-serving supplement — see the refinement below.
    servingUnitCode: z.number().optional(),
    servingUnitName: z.string().optional(),
    servingMultiplier: z.string().optional(),
    servingWeightG: z.string().optional(),
    source: z.string().optional(),
    sourceId: z.string().optional(),
    sourceWeb: z.string().optional(),
    sourcePdf: z.string().optional(),
    sourceConfidence: z.string().optional(),
    brand: z.string().optional(),
    favourite: z.boolean().optional(),
    homemade: z.boolean().optional(),
    texture: z.string().optional(),
    cuisineTags: z.array(z.number()).optional(),
    feedingCategories: z.array(z.number()).optional(),
    mealTypes: z.array(z.number()).optional(),
    conditionTags: z.array(z.number()).optional(),
    allergenTags: z.array(z.number()).optional(),
    ageSuitabilityTags: z.array(z.number()).optional(),
    contextTags: z.array(z.number()).optional(),
  })
  .catchall(z.string().optional())
  .superRefine((values, ctx) => {
    if (values.foodType !== SUPPLEMENT_FOOD_TYPE) return;
    if (!values.nutrientBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["nutrientBasis"],
        message:
          "Say what the label states its values against — this can't be changed later.",
      });
      return;
    }
    if (values.nutrientBasis !== "SERV") return;

    // A per-serving food is created together with its first serving in one
    // transaction, so the unit and the label multiplier have to be given here.
    // There is no second call to supply them, and the API rejects the food
    // itself if they are missing — which would lose the whole form.
    if (!values.servingUnitCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["servingUnitCode"],
        message:
          "Pick the unit the label is stated against — a capsule, a scoop, a sachet.",
      });
    }
    const multiplierError = validateServingMultiplier(
      values.servingMultiplier ?? ""
    );
    if (multiplierError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["servingMultiplier"],
        message: multiplierError,
      });
    }
    // Optional here — a capsule has no gram figure — but a typed one must be
    // a real weight.
    const weightError = validateServingWeightG(
      values.servingWeightG ?? "",
      false
    );
    if (weightError) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["servingWeightG"],
        message: weightError,
      });
    }
  });

type FoodEntryFormValues = z.infer<typeof foodEntrySchema>;

type FoodEntryTab = "info" | "classification" | "nutrients" | "servings";

// Which tab a field lives on, so a submit blocked by validation can put the
// user in front of the field that blocked it. Everything validated today is on
// Info; anything added elsewhere needs its entry here.
const TAB_FOR_FIELD: Record<string, FoodEntryTab> = {
  name: "info",
  nutrientBasis: "info",
  servingUnitCode: "info",
  servingMultiplier: "info",
  servingWeightG: "info",
};

const SCALAR_EMPTY_DEFAULTS: Partial<FoodEntryFormValues> = {
  name: "",
  hindiName: "",
  fullName: "",
  foodType: "",
  nutrientBasis: "",
  servingUnitCode: undefined,
  servingUnitName: "",
  servingMultiplier: "",
  servingWeightG: "",
  source: "",
  sourceId: "",
  sourceWeb: "",
  sourcePdf: "",
  sourceConfidence: "",
  brand: "",
  favourite: false,
  homemade: false,
  texture: "",
  cuisineTags: [],
  feedingCategories: [],
  mealTypes: [],
  conditionTags: [],
  allergenTags: [],
  ageSuitabilityTags: [],
  contextTags: [],
};

// ─────────────────────────────────────────────────────────────────────
// localStorage draft autosave — guards against tab close / refresh /
// nav-away during a long batch entry. Cleared on success / explicit
// reset / dismissal / 7-day TTL expiry.
// ─────────────────────────────────────────────────────────────────────

const DRAFT_KEY = "add-food-entry-draft";
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface Draft {
  values: Record<string, unknown>;
  savedAt: number;
}

const readDraft = (): Draft | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Draft;
    if (
      typeof parsed?.savedAt !== "number" ||
      Date.now() - parsed.savedAt > DRAFT_TTL_MS
    ) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const clearDraft = () => {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    /* no-op */
  }
};

const isEmptyFormValues = (v: Record<string, unknown>): boolean =>
  Object.values(v).every(
    (x) =>
      x === undefined ||
      x === "" ||
      x === false ||
      (Array.isArray(x) && x.length === 0)
  );

const formatRelativeTime = (timestamp: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const debounce = <T extends (...a: never[]) => void>(fn: T, ms: number) => {
  let t: ReturnType<typeof setTimeout> | undefined;
  return (...args: Parameters<T>) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
};

const NOOP = () => {};

// "Dirty" in Add means the user has put something in the field — i.e. the
// value differs from the empty default. We surface this visually with the
// same warning treatment Edit uses (border-warning on inputs, warning text
// on labels, warning-tint on nutrient rows) so reviewers can see at a glance
// what's been entered. There's no per-row commit involved.
const isFieldFilled = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (typeof value === "boolean") return value === true;
  if (Array.isArray(value)) return value.length > 0;
  return true;
};

// ─────────────────────────────────────────────────────────────────────

export const AddFoodEntryForm = () => {
  const { hasPermission } = useAuthStore();
  const {
    catalog,
    isLoading: isLoadingCatalog,
    isError: isCatalogError,
    refetch: refetchCatalog,
  } = useNutrientCatalog();

  if (!hasPermission(CONSOLE_PERMISSIONS.MANAGE_NUTRITION_DB)) {
    return <AccessDeniedCard feature="Add Food Entry" />;
  }

  if (isCatalogError) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-3">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <span className="z-body">Failed to load nutrient catalog.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refetchCatalog()}
            >
              Retry
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoadingCatalog || !catalog) {
    return (
      <div className="h-full flex flex-col min-h-0 gap-3">
        <Card className="flex-1 flex items-center justify-center">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            <span className="z-body">Loading nutrient catalog…</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <AddFoodEntryFormInner
      categories={catalog.categories}
      slugByFormField={catalog.slugByFormField}
    />
  );
};

interface AddFoodEntryFormInnerProps {
  categories: NutrientCategory[];
  slugByFormField: Record<string, string>;
}

const AddFoodEntryFormInner = ({
  categories,
  slugByFormField,
}: AddFoodEntryFormInnerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<FoodEntryTab>("info");
  const [reviewOpen, setReviewOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdFoodItem, setCreatedFoodItem] = useState<{
    id: number;
    name: string;
    // What the server says the basis is. The servings form is built from this,
    // never from what we asked for.
    nutrientBasis: FoodNutrientBasis;
    // What we asked for, kept only to detect a silent disagreement below.
    requestedBasis?: FoodNutrientBasis;
  } | null>(null);

  // Draft autosave UI state — `draftSavedAt` doubles as the
  // "is there a draft" flag for the inline indicator.
  const [draftSavedAt, setDraftSavedAt] = useState<number | null>(null);

  const { data: tagMetadata, isLoading: isLoadingMetadata } = useTagMetadata();

  // Catalog-derived structures. Built once per catalog reference.
  const { NUTRIENT_GROUPS, FIELD_LABEL_LOOKUP, emptyDefaults } = useMemo(() => {
    const generalCategoryFields =
      categories.find((c) => c.title === "General")?.fields ?? [];
    // Exactly what the backend's catalog lists -- Energy and Water included --
    // all written through nutrient_values (the backend routes Energy to its field).
    const generalTableFields: EditableNutrientField[] = generalCategoryFields;
    const groups: ReadonlyArray<{
      title: string;
      fields: EditableNutrientField[];
    }> = [
      { title: "General", fields: generalTableFields },
      ...categories
        .filter((c) => c.title !== "General")
        .map((c) => ({ title: c.title, fields: c.fields })),
    ];
    const names: string[] = [
      ...categories.flatMap((cat) => cat.fields.map((f) => f.name)),
    ];
    const lookup: Record<string, { label: string; unit?: string }> = {};
    generalCategoryFields.forEach((f) => {
      lookup[f.name] = { label: f.label, unit: f.unit };
    });
    categories.forEach((cat) =>
      cat.fields.forEach((f) => {
        lookup[f.name] = { label: f.label, unit: f.unit };
      })
    );
    const defaults: Partial<FoodEntryFormValues> = {
      ...SCALAR_EMPTY_DEFAULTS,
      ...Object.fromEntries(names.map((n) => [n, ""])),
    };
    return {
      NUTRIENT_GROUPS: groups,
      FIELD_LABEL_LOOKUP: lookup,
      emptyDefaults: defaults,
    };
  }, [categories]);

  const foodSources = tagMetadata?.sources_and_brands.food_sources ?? [];
  const foodBrands = tagMetadata?.sources_and_brands.food_brands ?? [];
  const sourceConfidenceOptions = tagMetadata?.choices.source_confidence ?? [];
  const textureOptions = tagMetadata?.choices.texture ?? [];

  const classificationTagMappings = [
    {
      fieldName: "cuisineTags",
      label: "Cuisine Tags",
      data: tagMetadata?.classification_tags.cuisine_tags ?? [],
    },
    {
      fieldName: "feedingCategories",
      label: "Feeding Method",
      data: tagMetadata?.classification_tags.feeding_categories ?? [],
    },
    {
      fieldName: "mealTypes",
      label: "Meal Context",
      data: tagMetadata?.classification_tags.meal_types ?? [],
    },
    {
      fieldName: "conditionTags",
      label: "Condition Suitability",
      data: tagMetadata?.classification_tags.condition_tags ?? [],
    },
    {
      fieldName: "allergenTags",
      label: "Allergen Tags",
      data: tagMetadata?.classification_tags.allergen_tags ?? [],
    },
    {
      fieldName: "ageSuitabilityTags",
      label: "Age Suitability",
      data: tagMetadata?.classification_tags.age_suitability_tags ?? [],
    },
    {
      fieldName: "contextTags",
      label: "Context Tags",
      data: tagMetadata?.classification_tags.context_tags ?? [],
    },
  ] as const;

  const form = useForm<FoodEntryFormValues>({
    resolver: zodResolver(foodEntrySchema),
    defaultValues: emptyDefaults as FoodEntryFormValues,
    mode: "onSubmit",
  });

  const watchedFormValues = form.watch();
  const watchedFoodType = watchedFormValues.foodType as string | undefined;
  const watchedNutrientBasis = watchedFormValues.nutrientBasis as
    | string
    | undefined;

  // Dirty tracking — fields whose current value differs from the empty
  // default. Drives label/input warning styling on Info & Classification
  // and the warning-row tint inside <EditableNutrientTable>.
  const dirtyFields: Set<string> = new Set(
    Object.entries(watchedFormValues as Record<string, unknown>)
      .filter(([, value]) => isFieldFilled(value))
      .map(([name]) => name)
  );
  const isDirty = (name: string) => dirtyFields.has(name);

  // The nested serving belongs to a per-serving supplement and nothing else.
  // Wipe it the moment the food stops being one, so a leftover multiplier
  // can't ride along in the autosaved draft or the create payload.
  const clearServingFields = () => {
    form.setValue("servingUnitCode", undefined);
    form.setValue("servingUnitName", "");
    form.setValue("servingMultiplier", "");
    form.setValue("servingWeightG", "");
  };

  const servingUnitName =
    (watchedFormValues.servingUnitName as string | undefined)?.trim() ?? "";
  const servingUnitLabel = servingUnitName || "unit";

  // Auto-restore any saved draft on mount. The persisted draft is the
  // single source of truth — no banner / opt-in. If the user wants to
  // start fresh they hit Discard or Reset.
  useEffect(() => {
    const d = readDraft();
    if (d?.values) {
      form.reset({ ...emptyDefaults, ...d.values } as FoodEntryFormValues);
      setDraftSavedAt(d.savedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced autosave on every form change. Empty form clears the draft
  // (and the inline indicator); non-empty form writes and refreshes the
  // "saved at" timestamp shown in the header.
  useEffect(() => {
    const debounced = debounce((v: Record<string, unknown>) => {
      if (isEmptyFormValues(v)) {
        clearDraft();
        setDraftSavedAt(null);
        return;
      }
      const now = Date.now();
      try {
        localStorage.setItem(
          DRAFT_KEY,
          JSON.stringify({ values: v, savedAt: now })
        );
        setDraftSavedAt(now);
      } catch {
        /* quota / privacy mode — silently no-op */
      }
    }, 500);
    const sub = form.watch((v) => debounced(v as Record<string, unknown>));
    return () => sub.unsubscribe();
  }, [form]);

  const discardDraft = () => {
    form.reset(emptyDefaults as FoodEntryFormValues);
    clearDraft();
    setDraftSavedAt(null);
  };

  const handleReset = () => {
    form.reset(emptyDefaults as FoodEntryFormValues);
    clearDraft();
    setDraftSavedAt(null);
    setActiveTab("info");
  };

  const handleAddAnotherFood = () => {
    setCreatedFoodItem(null);
    form.reset(emptyDefaults as FoodEntryFormValues);
    clearDraft();
    setDraftSavedAt(null);
    setActiveTab("info");
  };

  // ─────────────────────────────────────────────────────────────────────
  // Leave-page guard. Browser-level only (tab close, refresh, address
  // bar): React Router v6 with <BrowserRouter> doesn't expose a working
  // useBlocker — that's data-router only. For in-app navigation we rely
  // on the localStorage autosave: the draft is restored on return.
  // ─────────────────────────────────────────────────────────────────────
  const hasUnsavedChanges = dirtyFields.size > 0 && !createdFoodItem;
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  // ─────────────────────────────────────────────────────────────────────
  // Submit flow — single batch POST.
  // ─────────────────────────────────────────────────────────────────────

  const handleReview = (_values: FoodEntryFormValues) => {
    setReviewOpen(true);
  };

  // Submit is reachable from every tab, but a blocked submit used to show
  // nothing at all when the offending field was on another one — it read as a
  // dead button. Go to the field that blocked it and say what it wants.
  const handleInvalid = (errors: FieldErrors<FoodEntryFormValues>) => {
    const firstField = Object.keys(errors)[0];
    setActiveTab(TAB_FOR_FIELD[firstField] ?? "info");
    const message = errors[firstField as keyof typeof errors]?.message;
    toast({
      title: "Some details are missing",
      description:
        typeof message === "string"
          ? message
          : "Check the highlighted fields before saving.",
      variant: "destructive",
    });
  };

  const handleConfirmCreate = async () => {
    setIsSubmitting(true);
    try {
      const v = form.getValues();
      const payload: FoodItemPayload = { name: v.name as string };

      if (v.hindiName) payload.hindi_name = v.hindiName;
      if (v.fullName) payload.full_name = v.fullName;
      if (v.foodType) payload.food_type = v.foodType;
      // Create-only, and rejected on any food type but supplement.
      if (v.foodType === SUPPLEMENT_FOOD_TYPE && v.nutrientBasis) {
        payload.nutrient_basis = v.nutrientBasis as FoodNutrientBasis;
        // The nested serving is required on "SERV" and rejected on "100G", so
        // it is sent on exactly one of the two. Zod has already established
        // the unit and multiplier are there by this point.
        if (v.nutrientBasis === "SERV" && v.servingUnitCode) {
          const servingWeight = (
            v.servingWeightG as string | undefined
          )?.trim();
          payload.serving = {
            serving_unit: v.servingUnitCode as number,
            multiplier: ((v.servingMultiplier as string) ?? "").trim(),
            // An explicit null, not an omission: "this label states no gram
            // figure" is a fact about the supplement, not a field we forgot.
            weight_g: servingWeight || null,
          };
        }
      }
      if (v.source) payload.source = v.source;
      if (v.sourceId) payload.source_id = v.sourceId;
      if (v.sourceWeb) payload.source_web = v.sourceWeb;
      if (v.sourcePdf) payload.source_pdf = v.sourcePdf;
      if (v.brand) payload.brand = v.brand;
      if (v.favourite !== undefined) payload.is_favorite = v.favourite;
      if (v.homemade !== undefined) payload.is_homemade = v.homemade;

      const nutrientValues = buildNutrientValues(v, slugByFormField);
      if (Object.keys(nutrientValues).length > 0) {
        payload.nutrient_values = nutrientValues;
      }

      const tagInfo: NonNullable<FoodItemPayload["tag_info"]> = {};
      if (v.texture) tagInfo.texture = v.texture;
      if (v.sourceConfidence) tagInfo.source_confidence = v.sourceConfidence;
      if (v.cuisineTags?.length) tagInfo.cuisine_tag_ids = v.cuisineTags;
      if (v.feedingCategories?.length)
        tagInfo.feeding_category_ids = v.feedingCategories;
      if (v.mealTypes?.length) tagInfo.meal_type_ids = v.mealTypes;
      if (v.conditionTags?.length) tagInfo.condition_tag_ids = v.conditionTags;
      if (v.allergenTags?.length) tagInfo.allergen_tag_ids = v.allergenTags;
      if (v.ageSuitabilityTags?.length)
        tagInfo.age_suitability_tag_ids = v.ageSuitabilityTags;
      if (v.contextTags?.length) tagInfo.context_tag_ids = v.contextTags;
      if (Object.keys(tagInfo).length > 0) payload.tag_info = tagInfo;

      const response = await nutritionAPI.foodItems.create(payload);

      await queryClient.invalidateQueries(
        invalidateQueries.nutritionFoodItems()
      );

      setCreatedFoodItem({
        id: response.id,
        name: response.name,
        nutrientBasis: response.nutrient_basis ?? "100G",
        requestedBasis:
          v.foodType === SUPPLEMENT_FOOD_TYPE && v.nutrientBasis
            ? (v.nutrientBasis as FoodNutrientBasis)
            : undefined,
      });
      clearDraft();
      setDraftSavedAt(null);
      setReviewOpen(false);
      setSuccessOpen(true);
    } catch (error: unknown) {
      const isPermissionError = getApiErrorStatus(error) === 403;
      toast({
        title: isPermissionError ? "Access Denied" : "Error",
        description: isPermissionError
          ? "Your permissions no longer allow nutrition database changes."
          : getApiErrorMessage(
              error,
              "Failed to add food entry. Please try again."
            ),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessConfirm = () => {
    setSuccessOpen(false);
    setActiveTab("servings");
  };

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <Card className="max-w-[2000px] flex-1 flex flex-col min-h-0 overflow-visible">
        <CardHeader className="shrink-0">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-wrap">
              <CardTitle className="z-h2">Add New Food Entry</CardTitle>
              {hasUnsavedChanges && <Badge variant="warning">Editing</Badge>}
            </div>
            {createdFoodItem ? (
              <Button
                onClick={handleAddAnotherFood}
                variant="outline"
                size="sm"
              >
                Add Another Food
              </Button>
            ) : draftSavedAt !== null ? (
              <div className="flex flex-col items-end gap-1">
                <Button
                  type="button"
                  onClick={discardDraft}
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                >
                  Discard draft
                </Button>
                <span className="z-caption text-muted-foreground inline-flex items-center gap-1.5">
                  <span
                    className="size-1.5 rounded-full bg-warning"
                    aria-hidden
                  />
                  Draft saved {formatRelativeTime(draftSavedAt)}
                </span>
              </div>
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pt-2 pb-6">
          <Alert className="mb-4">
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>Important:</strong> Only fill in fields for which you have
              actual values. Leave fields empty if you don&apos;t have data —
              don&apos;t enter 0 or blank values.
            </AlertDescription>
          </Alert>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(handleReview, handleInvalid)}
              className="space-y-4"
            >
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as typeof activeTab)}
              >
                <TabsList>
                  <TabsTrigger value="info">Info</TabsTrigger>
                  <TabsTrigger value="classification">
                    Classification &amp; Diet
                  </TabsTrigger>
                  <TabsTrigger value="nutrients">Nutrient Profile</TabsTrigger>
                  <TabsTrigger value="servings">Servings</TabsTrigger>
                </TabsList>

                {/* ─── INFO TAB ──────────────────────────────────────── */}
                <TabsContent value="info" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 pb-5 space-y-4">
                      {/* Name fields - 2 per row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-4">
                        {nameFields.map((field) => {
                          const dirty = isDirty(field.name);
                          return (
                            <FormField
                              key={field.name}
                              control={form.control}
                              name={field.name as keyof FoodEntryFormValues}
                              render={({ field: formField }) => (
                                <FormItem>
                                  <div className="flex items-center gap-4">
                                    <FormLabel
                                      className={cn(
                                        "z-label w-[120px] shrink-0 mb-0",
                                        dirty && "text-warning font-semibold"
                                      )}
                                    >
                                      {field.label}
                                      {field.name === "name" && (
                                        <RequiredMark className="ml-1" />
                                      )}
                                    </FormLabel>
                                    <FormControl>
                                      <Input
                                        placeholder="Enter value"
                                        className={cn(
                                          "flex-1",
                                          dirty && "border-warning border-2"
                                        )}
                                        value={
                                          (formField.value as string) ?? ""
                                        }
                                        onChange={formField.onChange}
                                        onBlur={formField.onBlur}
                                        name={formField.name}
                                        ref={formField.ref}
                                      />
                                    </FormControl>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          );
                        })}

                        {/* Food Type */}
                        <FormField
                          control={form.control}
                          name="foodType"
                          render={({ field }) => {
                            const dirty = isDirty("foodType");
                            return (
                              <FormItem>
                                <div className="flex items-center gap-4">
                                  <FormLabel
                                    className={cn(
                                      "z-label w-[120px] shrink-0 mb-0",
                                      dirty && "text-warning font-semibold"
                                    )}
                                  >
                                    Food Type
                                  </FormLabel>
                                  <Select
                                    onValueChange={(value) => {
                                      field.onChange(value);
                                      // Basis only applies to supplements —
                                      // don't carry a stale choice onto another
                                      // food type where the API would reject it.
                                      if (value !== SUPPLEMENT_FOOD_TYPE) {
                                        form.setValue("nutrientBasis", "");
                                        clearServingFields();
                                      }
                                    }}
                                    value={(field.value as string) ?? ""}
                                  >
                                    <FormControl>
                                      <SelectTrigger
                                        className={cn(
                                          "flex-1 max-w-[400px]",
                                          dirty && "border-warning border-2"
                                        )}
                                      >
                                        <SelectValue placeholder="Select food type" />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {FOOD_TYPE_OPTIONS.map((option) => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <FormMessage />
                              </FormItem>
                            );
                          }}
                        />

                        {/* Nutrient Basis — supplements only, and only here:
                            the API refuses to change it once the food exists,
                            because flipping it would rescale every entry
                            already logged against this food. */}
                        {watchedFoodType === SUPPLEMENT_FOOD_TYPE && (
                          <FormField
                            control={form.control}
                            name="nutrientBasis"
                            render={({ field }) => {
                              const dirty = isDirty("nutrientBasis");
                              return (
                                <FormItem>
                                  <div className="flex items-center gap-4">
                                    <FormLabel
                                      className={cn(
                                        "z-label w-[120px] shrink-0 mb-0",
                                        dirty && "text-warning font-semibold"
                                      )}
                                    >
                                      What does the label say? <RequiredMark />
                                    </FormLabel>
                                    <Select
                                      onValueChange={(value) => {
                                        field.onChange(value);
                                        // A serving is only accepted on a
                                        // per-serving food; keeping a typed one
                                        // around would send the API a payload
                                        // it rejects outright.
                                        if (value !== "SERV") {
                                          clearServingFields();
                                        }
                                      }}
                                      value={(field.value as string) ?? ""}
                                    >
                                      <FormControl>
                                        <SelectTrigger
                                          className={cn(
                                            "flex-1 max-w-[400px]",
                                            dirty && "border-warning border-2"
                                          )}
                                        >
                                          <SelectValue placeholder="Select nutrient basis" />
                                        </SelectTrigger>
                                      </FormControl>
                                      <SelectContent>
                                        {FOOD_NUTRIENT_BASIS_OPTIONS.map(
                                          (option) => (
                                            <SelectItem
                                              key={option.value}
                                              value={option.value}
                                            >
                                              {option.label}
                                            </SelectItem>
                                          )
                                        )}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <p className="z-caption text-muted-foreground ml-[136px]">
                                    e.g. "Per 40 g serve" or "Each capsule
                                    contains&hellip;" &rarr; Per serving. Locked
                                    once saved.
                                  </p>
                                  <FormMessage />
                                </FormItem>
                              );
                            }}
                          />
                        )}

                        {/* The food's first serving. It goes nested inside the
                            create call — a per-serving food and its serving are
                            created in one transaction, so unlike every other
                            food there is no add-it-afterwards step for this
                            one, and the food itself won't save without it. */}
                        {watchedFoodType === SUPPLEMENT_FOOD_TYPE &&
                          watchedNutrientBasis === "SERV" && (
                            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                              <div>
                                <p className="z-label text-foreground">
                                  The serving this label describes
                                </p>
                                <p className="z-caption text-muted-foreground mt-1">
                                  Saved together with the food. Any other
                                  servings go in from the Servings tab
                                  afterwards.
                                </p>
                              </div>

                              <FormField
                                control={form.control}
                                name="servingUnitCode"
                                render={({ field }) => (
                                  <FormItem>
                                    <div className="flex items-center gap-4">
                                      <FormLabel className="z-label w-[120px] shrink-0 mb-0">
                                        Serving unit <RequiredMark />
                                      </FormLabel>
                                      <div className="flex-1 max-w-[400px]">
                                        <ServingUnitCombobox
                                          valueCode={
                                            (field.value as
                                              | number
                                              | undefined) ?? null
                                          }
                                          valueName={servingUnitName}
                                          onSelect={(code, name) => {
                                            field.onChange(code);
                                            form.setValue(
                                              "servingUnitName",
                                              name
                                            );
                                          }}
                                        />
                                      </div>
                                    </div>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="servingMultiplier"
                                render={({ field }) => (
                                  <FormItem>
                                    <div className="flex items-center gap-4">
                                      <FormLabel className="z-label w-[120px] shrink-0 mb-0">
                                        Label servings <RequiredMark />
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          value={(field.value as string) ?? ""}
                                          inputMode="decimal"
                                          placeholder="e.g. 0.5"
                                          className="flex-1 max-w-[400px]"
                                        />
                                      </FormControl>
                                    </div>
                                    <p className="z-caption text-muted-foreground ml-[136px]">
                                      How many label servings one{" "}
                                      {servingUnitLabel} is. A panel printed
                                      &ldquo;per 2 tablets&rdquo; makes one
                                      tablet 0.5. Read it off the label — the
                                      unit&rsquo;s name doesn&rsquo;t tell you.
                                    </p>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />

                              <FormField
                                control={form.control}
                                name="servingWeightG"
                                render={({ field }) => (
                                  <FormItem>
                                    <div className="flex items-center gap-4">
                                      <FormLabel className="z-label w-[120px] shrink-0 mb-0">
                                        Weight (g)
                                      </FormLabel>
                                      <FormControl>
                                        <Input
                                          {...field}
                                          value={(field.value as string) ?? ""}
                                          inputMode="decimal"
                                          placeholder="Leave blank if the label states none"
                                          className="flex-1 max-w-[400px]"
                                        />
                                      </FormControl>
                                    </div>
                                    <p className="z-caption text-muted-foreground ml-[136px]">
                                      Optional — a capsule or a tablet usually
                                      has no gram figure to give.
                                    </p>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}
                      </div>

                      {/* Source */}
                      <FormField
                        control={form.control}
                        name="source"
                        render={({ field }) => {
                          const dirty = isDirty("source");
                          return (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={cn(
                                    "z-label w-[170px] shrink-0 mb-0",
                                    dirty && "text-warning font-semibold"
                                  )}
                                >
                                  Source
                                </FormLabel>
                                <Select
                                  onValueChange={(value) => {
                                    field.onChange(value);
                                    const selected = foodSources.find(
                                      (s) => s.name === value
                                    );
                                    if (selected) {
                                      form.setValue(
                                        "sourceId",
                                        selected.id.toString()
                                      );
                                    }
                                  }}
                                  value={(field.value as string) ?? ""}
                                  disabled={isLoadingMetadata}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        "flex-1 max-w-[400px]",
                                        dirty && "border-warning border-2"
                                      )}
                                    >
                                      <SelectValue
                                        placeholder={
                                          isLoadingMetadata
                                            ? "Loading..."
                                            : "Select source"
                                        }
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {foodSources.map((source) => (
                                      <SelectItem
                                        key={source.id}
                                        value={source.name}
                                      >
                                        {source.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      {/* Source Web / PDF */}
                      {(
                        [
                          { name: "sourceWeb", label: "Source Web" },
                          { name: "sourcePdf", label: "Source PDF" },
                        ] as const
                      ).map((field) => {
                        const dirty = isDirty(field.name);
                        return (
                          <FormField
                            key={field.name}
                            control={form.control}
                            name={field.name as keyof FoodEntryFormValues}
                            render={({ field: formField }) => (
                              <FormItem>
                                <div className="flex items-center gap-4">
                                  <FormLabel
                                    className={cn(
                                      "z-label w-[170px] shrink-0 mb-0",
                                      dirty && "text-warning font-semibold"
                                    )}
                                  >
                                    {field.label}
                                  </FormLabel>
                                  <FormControl>
                                    <Input
                                      placeholder="Enter URL"
                                      className={cn(
                                        "flex-1",
                                        dirty && "border-warning border-2"
                                      )}
                                      value={(formField.value as string) ?? ""}
                                      onChange={formField.onChange}
                                      onBlur={formField.onBlur}
                                      name={formField.name}
                                      ref={formField.ref}
                                    />
                                  </FormControl>
                                </div>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        );
                      })}

                      {/* Source Confidence */}
                      <FormField
                        control={form.control}
                        name="sourceConfidence"
                        render={({ field }) => {
                          const dirty = isDirty("sourceConfidence");
                          return (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={cn(
                                    "z-label w-[170px] shrink-0 mb-0",
                                    dirty && "text-warning font-semibold"
                                  )}
                                >
                                  Source Confidence
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={(field.value as string) ?? ""}
                                  disabled={isLoadingMetadata}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        "flex-1 max-w-[400px]",
                                        dirty && "border-warning border-2"
                                      )}
                                    >
                                      <SelectValue
                                        placeholder={
                                          isLoadingMetadata
                                            ? "Loading..."
                                            : "Select source confidence"
                                        }
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {sourceConfidenceOptions.map((option) => (
                                      <SelectItem
                                        key={option.value}
                                        value={option.value}
                                      >
                                        {option.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      {/* Brand */}
                      <FormField
                        control={form.control}
                        name="brand"
                        render={({ field }) => {
                          const dirty = isDirty("brand");
                          return (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={cn(
                                    "z-label w-[170px] shrink-0 mb-0",
                                    dirty && "text-warning font-semibold"
                                  )}
                                >
                                  Brand
                                </FormLabel>
                                <Select
                                  onValueChange={field.onChange}
                                  value={(field.value as string) ?? ""}
                                  disabled={isLoadingMetadata}
                                >
                                  <FormControl>
                                    <SelectTrigger
                                      className={cn(
                                        "flex-1 max-w-[400px]",
                                        dirty && "border-warning border-2"
                                      )}
                                    >
                                      <SelectValue
                                        placeholder={
                                          isLoadingMetadata
                                            ? "Loading..."
                                            : "Select brand"
                                        }
                                      />
                                    </SelectTrigger>
                                  </FormControl>
                                  <SelectContent>
                                    {foodBrands.map((brand) => (
                                      <SelectItem
                                        key={brand.id}
                                        value={brand.name}
                                      >
                                        {brand.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />

                      {/* Boolean toggles */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-3 pt-2">
                        {booleanFields.map((field) => {
                          const dirty = isDirty(field.name);
                          return (
                            <FormField
                              key={field.name}
                              control={form.control}
                              name={field.name as keyof FoodEntryFormValues}
                              render={({ field: formField }) => (
                                <FormItem>
                                  <div className="flex items-center gap-3">
                                    <FormLabel
                                      className={cn(
                                        "z-label shrink-0 mb-0",
                                        dirty && "text-warning font-semibold"
                                      )}
                                    >
                                      {field.label}
                                    </FormLabel>
                                    <FormControl>
                                      <Switch
                                        checked={Boolean(formField.value)}
                                        onCheckedChange={formField.onChange}
                                      />
                                    </FormControl>
                                  </div>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── CLASSIFICATION TAB ────────────────────────────── */}
                <TabsContent value="classification" className="mt-4">
                  <Card>
                    <CardContent className="space-y-6 pt-6">
                      <div className="rounded-lg border border-border/60 overflow-hidden">
                        {classificationTagMappings.map((mapping) => (
                          <FormField
                            key={mapping.fieldName}
                            control={form.control}
                            name={
                              mapping.fieldName as keyof FoodEntryFormValues
                            }
                            render={({ field }) => {
                              const selectedIds =
                                (field.value as number[]) ?? [];
                              return (
                                <div className="px-3">
                                  <TagPickerRow
                                    label={mapping.label}
                                    options={mapping.data}
                                    selectedIds={selectedIds}
                                    onChange={(ids) => field.onChange(ids)}
                                    isModified={selectedIds.length > 0}
                                    isLoading={isLoadingMetadata}
                                  />
                                </div>
                              );
                            }}
                          />
                        ))}
                      </div>

                      <FormField
                        control={form.control}
                        name="texture"
                        render={({ field }) => {
                          const dirty = isDirty("texture");
                          return (
                            <FormItem>
                              <div className="flex items-center gap-4">
                                <FormLabel
                                  className={cn(
                                    "z-label w-[160px] shrink-0 mb-0",
                                    dirty && "text-warning font-semibold"
                                  )}
                                >
                                  Texture
                                </FormLabel>
                                <div className="flex-1 max-w-[400px]">
                                  <Select
                                    onValueChange={field.onChange}
                                    value={(field.value as string) ?? ""}
                                    disabled={isLoadingMetadata}
                                  >
                                    <FormControl>
                                      <SelectTrigger
                                        className={cn(
                                          dirty && "border-warning border-2"
                                        )}
                                      >
                                        <SelectValue
                                          placeholder={
                                            isLoadingMetadata
                                              ? "Loading..."
                                              : "Select texture"
                                          }
                                        />
                                      </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                      {textureOptions.map((option) => (
                                        <SelectItem
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {option.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              </div>
                              <FormMessage />
                            </FormItem>
                          );
                        }}
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── NUTRIENTS TAB ────────────────────────────────── */}
                <TabsContent value="nutrients" className="mt-4">
                  <Card>
                    <CardContent className="pt-6 space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <h3 className="z-h3">Nutrient Profile</h3>
                          {/* The grid takes raw numbers with no stated basis,
                              which is only unambiguous while every food is
                              per-100 g. Say which one applies. */}
                          <p className="z-caption text-muted-foreground mt-1">
                            {watchedFoodType === SUPPLEMENT_FOOD_TYPE &&
                            watchedNutrientBasis === "SERV"
                              ? "Enter values per one serving, exactly as printed on the label (per strip, per tablet, per scoop)."
                              : "Enter values per 100 g of this food."}
                          </p>
                        </div>
                        <span className="z-caption text-muted-foreground shrink-0">
                          Values save when you click Review &amp; Create.
                        </span>
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {NUTRIENT_GROUPS.map((group) => (
                          <EditableNutrientTable
                            key={group.title}
                            title={group.title}
                            fields={group.fields}
                            values={watchedFormValues as Record<string, string>}
                            dirty={dirtyFields}
                            hideRowActions
                            onChange={(name, value) =>
                              form.setValue(
                                name as keyof FoodEntryFormValues,
                                value
                              )
                            }
                            onCommit={NOOP}
                            onRevert={NOOP}
                          />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* ─── SERVINGS TAB ─────────────────────────────────── */}
                <TabsContent value="servings" className="mt-4">
                  {createdFoodItem ? (
                    <div className="space-y-4">
                      {/* The servings form is built from the basis the server
                          reports, not the one we asked for. If those disagree,
                          say so — silently falling back to per-100 g would ask
                          for a weight on a food stored per serving, and the
                          save would fail with an opaque field error. */}
                      {createdFoodItem.requestedBasis &&
                        createdFoodItem.requestedBasis !==
                          createdFoodItem.nutrientBasis && (
                          <Alert variant="destructive">
                            <AlertDescription>
                              You created this as{" "}
                              <strong>
                                {
                                  FOOD_NUTRIENT_BASIS_LABELS[
                                    createdFoodItem.requestedBasis
                                  ]
                                }
                              </strong>
                              , but the server reports{" "}
                              <strong>
                                {
                                  FOOD_NUTRIENT_BASIS_LABELS[
                                    createdFoodItem.nutrientBasis
                                  ]
                                }
                              </strong>
                              . Don't add servings until this is resolved — they
                              would be stored against the wrong basis.
                            </AlertDescription>
                          </Alert>
                        )}
                      <FoodServingsCard
                        foodItemId={createdFoodItem.id}
                        foodItemName={createdFoodItem.name}
                        nutrientBasis={createdFoodItem.nutrientBasis}
                      />
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="py-12">
                        <div className="rounded-lg border border-dashed border-border p-10 text-center">
                          <p className="z-body text-foreground">
                            No servings yet
                          </p>
                          <p className="z-caption text-muted-foreground mt-1">
                            Save the food item first, then add serving sizes
                            here.
                          </p>
                          <Button
                            type="button"
                            disabled
                            className="mt-4"
                            title="Save the food item first to add servings"
                          >
                            + Add Serving
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              </Tabs>

              {/* Bottom actions — hidden on Servings (it has its own
                  internal create flow). */}
              {activeTab !== "servings" && (
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={isSubmitting}
                  >
                    Reset
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Review & Create"
                    )}
                  </Button>
                </div>
              )}
            </form>
          </Form>

          {/* Review dialog ─────────────────────────────────────────── */}
          <AlertDialog open={reviewOpen} onOpenChange={setReviewOpen}>
            <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <AlertDialogHeader>
                <AlertDialogTitle>Review Food Item</AlertDialogTitle>
                <AlertDialogDescription>
                  Confirm the values below before creating the food item.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <ReviewSummary
                values={form.getValues()}
                classificationTagMappings={classificationTagMappings}
                fieldLabelLookup={FIELD_LABEL_LOOKUP}
              />
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isSubmitting}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleConfirmCreate}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    "Create Food Item"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Success dialog ────────────────────────────────────────── */}
          <AlertDialog open={successOpen} onOpenChange={setSuccessOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Food item created</AlertDialogTitle>
                <AlertDialogDescription>
                  {!createdFoodItem
                    ? "The food item has been added."
                    : createdFoodItem.nutrientBasis === "SERV"
                      ? `${createdFoodItem.name} has been added, along with the serving you entered. You can add more serving sizes for it.`
                      : `${createdFoodItem.name} has been added. You can now add serving sizes for it.`}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction onClick={handleSuccessConfirm}>
                  {createdFoodItem?.nutrientBasis === "SERV"
                    ? "View Servings"
                    : "Add Servings"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Review summary — extracted so the main render stays scannable.
// ─────────────────────────────────────────────────────────────────────

interface ReviewSummaryProps {
  values: FoodEntryFormValues;
  classificationTagMappings: ReadonlyArray<{
    fieldName: string;
    label: string;
    data: ReadonlyArray<{ id: number; name: string }>;
  }>;
  fieldLabelLookup: Record<string, { label: string; unit?: string }>;
}

const ReviewSummary = ({
  values,
  classificationTagMappings,
  fieldLabelLookup,
}: ReviewSummaryProps) => {
  const v = values as Record<string, unknown>;

  const basicEntries: Array<{ label: string; value: string }> = [];
  const push = (label: string, raw: unknown, suffix = "") => {
    if (raw === undefined || raw === null || raw === "") return;
    basicEntries.push({ label, value: `${String(raw)}${suffix}` });
  };
  push("Name", v.name);
  push("Hindi Name", v.hindiName);
  push("Full Name", v.fullName);
  // Stored as the single-letter code ("R"); the summary shows the label.
  push("Food Type", v.foodType ? getFoodTypeLabel(String(v.foodType)) : "");
  if (v.foodType === SUPPLEMENT_FOOD_TYPE && v.nutrientBasis) {
    push(
      "Nutrient Basis",
      FOOD_NUTRIENT_BASIS_LABELS[v.nutrientBasis as FoodNutrientBasis]
    );
    // The serving is created with the food, so it belongs in the thing the
    // user is asked to confirm — not just on the tab they typed it into.
    if (v.nutrientBasis === "SERV" && v.servingUnitCode) {
      push("Serving Unit", v.servingUnitName);
      push("Label Servings Per Unit", v.servingMultiplier);
      push("Serving Weight", v.servingWeightG, " g");
    }
  }
  push("Source", v.source);
  push("Source Web", v.sourceWeb);
  push("Source PDF", v.sourcePdf);
  push("Source Confidence", v.sourceConfidence);
  push("Brand", v.brand);
  if (v.favourite !== undefined && v.favourite !== false)
    basicEntries.push({ label: "Favorite", value: v.favourite ? "Yes" : "No" });
  if (v.homemade !== undefined && v.homemade !== false)
    basicEntries.push({ label: "Homemade", value: v.homemade ? "Yes" : "No" });
  if (v.texture)
    basicEntries.push({ label: "Texture", value: String(v.texture) });

  const tagEntries: Array<{ label: string; value: string }> = [];
  classificationTagMappings.forEach((m) => {
    const ids = (v[m.fieldName] as number[] | undefined) ?? [];
    if (!ids.length) return;
    const names = ids
      .map((id) => m.data.find((t) => t.id === id)?.name)
      .filter(Boolean) as string[];
    if (names.length)
      tagEntries.push({ label: m.label, value: names.join(", ") });
  });

  const nutrientEntries: Array<{ label: string; value: string }> = [];
  Object.keys(fieldLabelLookup).forEach((name) => {
    const raw = v[name];
    if (raw === undefined || raw === null || raw === "") return;
    const meta = fieldLabelLookup[name];
    nutrientEntries.push({
      label: meta.label,
      value: `${String(raw)}${meta.unit ? ` ${meta.unit}` : ""}`,
    });
  });

  if (
    basicEntries.length === 0 &&
    tagEntries.length === 0 &&
    nutrientEntries.length === 0
  ) {
    return (
      <p className="z-body text-muted-foreground">No values entered yet.</p>
    );
  }

  return (
    <div className="space-y-4">
      {basicEntries.length > 0 && (
        <div>
          <h3 className="z-h3 mb-2">Basic Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 z-body">
            {basicEntries.map((e) => (
              <div key={e.label} className="bg-muted/30 p-2 rounded-md">
                <strong>{e.label}:</strong> {e.value}
              </div>
            ))}
          </div>
        </div>
      )}

      {tagEntries.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="z-h3 mb-2">Classification</h3>
            <div className="space-y-2 z-body">
              {tagEntries.map((e) => (
                <div key={e.label} className="bg-muted/30 p-2 rounded-md">
                  <strong>{e.label}:</strong> {e.value}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {nutrientEntries.length > 0 && (
        <>
          <Separator />
          <div>
            <h3 className="z-h3 mb-2">Nutrients</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 z-body">
              {nutrientEntries.map((e) => (
                <div key={e.label} className="bg-muted/30 p-2 rounded-md">
                  <strong>{e.label}:</strong> {e.value}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AddFoodEntryForm;
